/**
 * VideoPlayerWidget Component
 * 
 * Integrated video player for video posts featuring:
 * - ReactPlayer for video playback (EXACT code from video-experiment)
 * - Real-time subtitle display from video post transcripts
 * - Player controls below the video
 * - Side-by-side layout: player on left, captions on right
 * - Expandable/collapsible interface
 * 
 * Used in: VideoPostTranscriptManager
 */
import React, { useState, useRef, useCallback } from 'react';
import ReactPlayer from 'react-player';
import {
    Box,
    Button,
    Paper,
    Typography,
    Grid,
    IconButton,
    Collapse,
    Tooltip
} from '@mui/material';
import {
    PlayArrow,
    Pause,
    Stop,
    VolumeUp,
    VolumeOff,
    Fullscreen,
    PictureInPicture,
    ExpandLess,
    ExpandMore,
    Edit
} from '@mui/icons-material';

// Subtitle component - memoized to prevent unnecessary re-renders
const SubtitleDisplay = React.memo(({ currentTime, transcripts }) => {
    const currentTranscript = transcripts.find(
        transcript => currentTime >= transcript.start && currentTime < transcript.end
    );

    return (
        <Paper sx={{ 
            minHeight: '300px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            p: 2,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {currentTranscript ? (
                <Box textAlign="center">
                    <Typography variant="h6" sx={{ lineHeight: 1.4, fontSize: '1.1rem' }}>
                        {currentTranscript.textContent.text || currentTranscript.textContent}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.7 }}>
                        {formatTime(currentTranscript.start)} - {formatTime(currentTranscript.end)}
                    </Typography>
                </Box>
            ) : (
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    No subtitle at current time
                </Typography>
            )}
        </Paper>
    );
});

// Helper function to format time for display
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VideoPlayerWidget = ({ post, transcripts = [], isExpanded, onToggleExpanded, onEditTranscript }) => {
    const playerRef = useRef(null);

    // EXACT initialState from video-experiment App.js
    const initialState = {
        src: post?.link,
        pip: false,
        playing: false,
        controls: true,
        light: false,
        volume: 1,
        muted: false,
        played: 0,
        loaded: 0,
        duration: 0,
        playbackRate: 1.0,
        loop: false,
        seeking: false,
        loadedSeconds: 0,
        playedSeconds: 0,
    };

    const [state, setState] = useState(initialState);

    // Detect Shorts by URL pattern
    const isShorts = typeof post?.link === 'string' && post.link.includes('/shorts/');
    const aspectRatio = isShorts ? '9/16' : '16/9';

    // EXACT handlers from video-experiment App.js with AbortError protection
    const handlePlayPause = () => {
        let isPaused = state.playing === false;
        // if it is stopped state (src is undefined and not playing)
        if (isPaused && state.src === undefined) {
            setState(prevState => ({ ...prevState, playing: true, src: post?.link }));
        } else {
            setState(prevState => ({ ...prevState, playing: !prevState.playing }));
        }
    };

    const handleStop = () => {
        setState(prevState => ({ ...prevState, src: undefined, playing: false }));
    };

    const handleVolumeChange = (event) => {
        setState(prevState => ({ ...prevState, volume: parseFloat(event.target.value) }));
    };

    const handleToggleMuted = () => {
        setState(prevState => ({ ...prevState, muted: !prevState.muted }));
    };

    const handleSetPlaybackRate = (event) => {
        setState(prevState => ({
            ...prevState,
            playbackRate: parseFloat(event.target.dataset.value)
        }));
    };

    const handleRateChange = (rate) => {
        console.log('onRateChange', rate);
        setState(prevState => ({ ...prevState, playbackRate: rate }));
    };

    const handleTogglePIP = () => {
        setState(prevState => ({ ...prevState, pip: !prevState.pip }));
    };

    const handlePlay = () => {
        console.log('onPlay');
        setState(prevState => ({ ...prevState, playing: true }));
    };

    const handleEnterPictureInPicture = () => {
        console.log('onEnterPictureInPicture');
        setState(prevState => ({ ...prevState, pip: true }));
    };

    const handleLeavePictureInPicture = () => {
        console.log('onLeavePictureInPicture');
        setState(prevState => ({ ...prevState, pip: false }));
    };

    const handlePause = () => {
        console.log('onPause');
        setState(prevState => ({ ...prevState, playing: false }));
    };

    const handleSeekMouseDown = () => {
        setState(prevState => ({ ...prevState, seeking: true }));
    };

    const handleSeekChange = (event) => {
        setState(prevState => ({ ...prevState, played: parseFloat(event.target.value) }));
    };

    const handleSeekMouseUp = (event) => {
        setState(prevState => ({ ...prevState, seeking: false }));
        if (playerRef.current) {
            playerRef.current.currentTime = Number.parseFloat(event.target.value) * playerRef.current.duration;
        }
    };

    const handleProgress = (state) => {
        const player = playerRef.current;
        // We only want to update time slider if we are not currently seeking
        if (!player || state.seeking || !player.buffered?.length) return;

        setState(prevState => ({
            ...prevState,
            loadedSeconds: player.buffered?.end(player.buffered?.length - 1),
            loaded: player.buffered?.end(player.buffered?.length - 1) / player.duration,
        }));
    };

    const handleTimeUpdate = () => {
        const player = playerRef.current;

        // We only want to update time slider if we are not currently seeking
        if (!player || state.seeking) return;

        if (!player.duration) return;

        setState(prevState => ({
            ...prevState,
            playedSeconds: player.currentTime,
            played: player.currentTime / player.duration,
        }));
    };

    const handleEnded = () => {
        console.log('onEnded');
        setState(prevState => ({ ...prevState, playing: prevState.loop }));
    };

    const handleDurationChange = () => {
        const player = playerRef.current;
        if (!player) return;

        const duration = player.duration;
        console.log('onDurationChange', duration);
        setState(prevState => ({ ...prevState, duration: duration }));
    };

    const handleClickFullscreen = () => {
        const reactPlayer = document.querySelector('.react-player');
        if (reactPlayer) {
            if (reactPlayer.requestFullscreen) {
                reactPlayer.requestFullscreen();
            } else if (reactPlayer.webkitRequestFullscreen) {
                reactPlayer.webkitRequestFullscreen();
            } else if (reactPlayer.mozRequestFullScreen) {
                reactPlayer.mozRequestFullScreen();
            } else if (reactPlayer.msRequestFullscreen) {
                reactPlayer.msRequestFullscreen();
            }
        }
    };

    const setPlayerRef = useCallback((player) => {
        if (!player) return;
        playerRef.current = player;
        console.log(player);
    }, []);

    const {
        src,
        playing,
        controls,
        light,
        volume,
        muted,
        loop,
        played,
        loaded,
        duration,
        playbackRate,
        pip,
        playedSeconds,
    } = state;

    // const SEPARATOR = ' · ';

    if (!post?.link) {
        return (
            <Button
                variant="outlined"
                onClick={onToggleExpanded}
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                disabled
            >
                No Video Link Available
            </Button>
        );
    }

    return (
        <Box className="video-player-widget">
            <Button
                variant="outlined"
                onClick={onToggleExpanded}
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ mb: 2 }}
                fullWidth
            >
                {isExpanded ? 'Hide Video Player' : 'Show Video Player'}
            </Button>

            <Collapse in={isExpanded}>
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        {/* Video Player - Left Side */}
                        <Grid item xs={12} lg={isShorts ? 5 : 6}>
                            <Box sx={{ mb: 2 }}>
                                <ReactPlayer
                                    ref={setPlayerRef}
                                    className="react-player"
                                    style={{ width: '100%', height: 'auto', aspectRatio }}
                                    src={src}
                                    pip={pip}
                                    playing={playing}
                                    controls={controls}
                                    light={light}
                                    loop={loop}
                                    playbackRate={playbackRate}
                                    volume={volume}
                                    muted={muted}
                                    config={{
                                        youtube: {
                                            color: 'white'
                                        },
                                        vimeo: {
                                            color: 'ffffff'
                                        }
                                    }}
                                    onLoadStart={() => console.log('onLoadStart')}
                                    onReady={() => console.log('onReady')}
                                    onStart={(e) => console.log('onStart', e)}
                                    onPlay={handlePlay}
                                    onEnterPictureInPicture={handleEnterPictureInPicture}
                                    onLeavePictureInPicture={handleLeavePictureInPicture}
                                    onPause={handlePause}
                                    onRateChange={handleRateChange}
                                    onSeeking={(e) => console.log('onSeeking', e)}
                                    onSeeked={(e) => console.log('onSeeked', e)}
                                    onEnded={handleEnded}
                                    onError={(e) => {
                                        console.log('onError', e);
                                        // Ignore common play/pause promise rejections
                                        if (e && e.message && e.message.includes('interrupted')) {
                                            return;
                                        }
                                    }}
                                    onTimeUpdate={handleTimeUpdate}
                                    onProgress={handleProgress}
                                    onDurationChange={handleDurationChange}
                                />
                            </Box>

                            {/* Controls Below Player - EXACT from video-experiment */}
                            <Box sx={{ mt: 2 }}>
                                {/* Playback Controls */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <IconButton onClick={handlePlayPause} color="primary">
                                        {playing ? <Pause /> : <PlayArrow />}
                                    </IconButton>
                                    <IconButton onClick={handleStop} color="primary">
                                        <Stop />
                                    </IconButton>
                                    <IconButton onClick={handleToggleMuted} color="primary">
                                        {muted ? <VolumeOff /> : <VolumeUp />}
                                    </IconButton>
                                    <IconButton onClick={handleClickFullscreen} color="primary">
                                        <Fullscreen />
                                    </IconButton>
                                    {src && ReactPlayer.canEnablePIP && ReactPlayer.canEnablePIP(src) && (
                                        <IconButton onClick={handleTogglePIP} color="primary">
                                            <PictureInPicture />
                                        </IconButton>
                                    )}
                                </Box>

                                {/* Seek Bar - EXACT from video-experiment */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" gutterBottom>
                                        Seek: {formatTime(playedSeconds)} / {formatTime(duration)}
                                    </Typography>
                                    <input
                                        type="range"
                                        min={0}
                                        max={0.999999}
                                        step="any"
                                        value={played}
                                        onMouseDown={handleSeekMouseDown}
                                        onChange={handleSeekChange}
                                        onMouseUp={handleSeekMouseUp}
                                        style={{ width: '100%' }}
                                    />
                                </Box>

                                {/* Volume Control - EXACT from video-experiment */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" gutterBottom>
                                        Volume: {Math.round(volume * 100)}%
                                    </Typography>
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step="any"
                                        value={volume}
                                        onChange={handleVolumeChange}
                                        style={{ width: '200px' }}
                                    />
                                </Box>

                                {/* Speed Controls - EXACT from video-experiment */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1 }}>
                                        Speed:
                                    </Typography>
                                    <button type="button" onClick={handleSetPlaybackRate} data-value={1}>1x</button>
                                    <button type="button" onClick={handleSetPlaybackRate} data-value={1.5}>1.5x</button>
                                    <button type="button" onClick={handleSetPlaybackRate} data-value={2}>2x</button>
                                </Box>

                                {/* Progress Bars - EXACT from video-experiment */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption">Played:</Typography>
                                        <progress max={1} value={played} style={{ width: '100%' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption">Loaded:</Typography>
                                        <progress max={1} value={loaded} style={{ width: '100%' }} />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Captions - Right Side */}
                        <Grid item xs={12} lg={isShorts ? 7 : 6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6">
                                    Live Captions
                                </Typography>
                                {onEditTranscript && transcripts.find(transcript => playedSeconds >= transcript.start && playedSeconds < transcript.end) && (
                                    <Tooltip title="Edit current transcript">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => {
                                                const currentTranscript = transcripts.find(
                                                    transcript => playedSeconds >= transcript.start && playedSeconds < transcript.end
                                                );
                                                if (currentTranscript) {
                                                    onEditTranscript(currentTranscript);
                                                }
                                            }}
                                            sx={{ color: 'primary.main' }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <SubtitleDisplay 
                                currentTime={playedSeconds} 
                                transcripts={transcripts}
                            />
                            
                            {transcripts.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    Add transcripts to this video to see live captions here.
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>
        </Box>
    );
};

export default VideoPlayerWidget; 